import { createRouter, createWebHistory, type Router } from 'vue-router';
import { useMetaAuthStore } from '@/stores/metaAuth';
import { useDomainStateStore } from '@/stores/navigation/domainStateStore';
import { TOP_LEVEL_ROUTES } from '@/composables/navigation/types';

const ENABLE_META_AUTH_MIDDLEWARE = true;

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/meta-auth',
      name: 'MetaAuth',
      component: () => import('@/pages/MetaAuthPage.vue'),
      meta: { requiresMetaAuth: false }
    },
    {
      path: '/onboarding',
      name: 'OnboardingPage',
      component: () => import('@/pages/OnboardingPage.vue'),
      meta: { requiresMetaAuth: false }
    },
    {
      path: '/',
      name: 'Home',
      component: () => import('@/pages/HomePage.vue'),
      meta: { requiresMetaAuth: true }
    },
    {
      path: '/auth',
      name: 'Auth',
      component: () => import('@/pages/AuthPage.vue'),
      meta: { requiresMetaAuth: true }
    },
    {
      path: '/map',
      name: 'Map',
      component: () => import('@/pages/MapPage.vue'),
      meta: { requiresMetaAuth: true }
    },
    {
      path: '/search',
      name: 'Search',
      component: () => import('@/pages/SearchPage.vue'),
      meta: { requiresMetaAuth: true }
    },
    {
      path: '/foryou',
      name: 'ForYou',
      component: () => import('@/pages/HomePage.vue'),
      meta: { requiresMetaAuth: true }
    },
    {
      path: '/foryou/search',
      name: 'ShareSearch',
      component: () => import('@/components/foryou/views/ShareSearchView.vue'),
      meta: { requiresMetaAuth: true }
    },
    {
      path: '/chat',
      name: 'Chat',
      component: () => import('@/pages/ChatPage.vue'),
      meta: { requiresMetaAuth: true }
    },
    {
      path: '/chat/new',
      name: 'ChatNew',
      component: () => import('@/components/chat/views/ChatNewChatView.vue'),
      meta: { requiresMetaAuth: true }
    },
    {
      path: '/chat/dm/:userId',
      name: 'ChatDM',
      component: () => import('@/components/chat/views/ChatDMView.vue'),
      meta: { requiresMetaAuth: true },
      props: true
    },
    {
      path: '/chat/group/:groupId',
      name: 'ChatGroupMessages',
      component: () => import('@/components/chat/views/ChatGroupMessagesView.vue'),
      meta: { requiresMetaAuth: true },
      props: true
    },
    {
      path: '/chat/group/:groupId/add-people',
      name: 'ChatAddPeople',
      component: () => import('@/components/chat/views/ChatAddPeopleView.vue'),
      meta: { requiresMetaAuth: true },
      props: true
    },
    {
      path: '/chat/dm/:userId/profile',
      name: 'ChatDMProfile',
      component: () => import('@/components/chat/views/ChatDMProfileView.vue'),
      meta: { requiresMetaAuth: true },
      props: true
    },
    {
      path: '/chat/dm/:userId/bookings',
      name: 'ChatDMProfileBookings',
      component: () => import('@/components/chat/views/ChatDMProfileBookingsView.vue'),
      meta: { requiresMetaAuth: true },
      props: true
    },
    {
      path: '/chat/dm/:userId/recently-sent',
      name: 'ChatDMProfileRecentlySent',
      component: () => import('@/components/chat/views/ChatDMProfileRecentlySentView.vue'),
      meta: { requiresMetaAuth: true },
      props: true
    },
    {
      path: '/chat/group/:groupId/profile',
      name: 'ChatGroupProfile',
      component: () => import('@/components/chat/views/ChatGroupProfileView.vue'),
      meta: { requiresMetaAuth: true },
      props: true
    },
    {
      path: '/chat/booking-confirmation',
      name: 'ChatBookingConfirmation',
      component: () => import('@/components/chat/views/ChatBookingConfirmationView.vue'),
      meta: { requiresMetaAuth: true }
    },
    {
      path: '/profile',
      name: 'Profile',
      component: () => import('@/pages/ProfilePage.vue'),
      meta: { requiresMetaAuth: true },
      props: false
    },
    {
      path: '/profile/edit',
      name: 'ProfileEdit',
      component: () => import('@/components/profile/views/ProfileEditView.vue'),
      meta: { requiresMetaAuth: true }
    },
    {
      path: '/profile/list/:listId/:listName',
      name: 'UserProfileListDetails',
      component: () => import('@/components/profile/views/UserProfileListDetailsView.vue'),
      meta: { requiresMetaAuth: true },
      props: true
    },
    {
      path: '/user/:id',
      name: 'UserProfile',
      component: () => import('@/pages/UserProfilePage.vue'),
      meta: { requiresMetaAuth: true },
      props: true
    },
    {
      path: '/user-v2/:id',
      name: 'UserProfileV2',
      component: () => import('@/pages/UserProfilePageV2.vue'),
      meta: { requiresMetaAuth: true },
      props: true
    },
    {
      path: '/user-v2/:id/list/:listId',
      name: 'UserListDetails',
      component: () => import('@/components/profile/views/UserProfileListDetailsView.vue'),
      meta: { requiresMetaAuth: true },
      props: true
    },
    {
      path: '/creator',
      name: 'CreatorProfile',
      component: () => import('@/pages/CreatorProfilePage.vue'),
      meta: { requiresMetaAuth: true }
    },
    {
      path: '/creator/:username',
      name: 'CreatorProfileUsername',
      component: () => import('@/pages/CreatorProfilePage.vue'),
      meta: { requiresMetaAuth: true }
    },
    {
      path: '/location/:id',
      name: 'LocationDetails',
      component: () => import('@/pages/LocationDetailsPage.vue'),
      props: true,
      meta: { requiresMetaAuth: true }
    },
    {
      path: '/booking/:bookingId',
      name: 'Booking',
      component: () => import('@/pages/BookingPage.vue'),
      props: true,
      meta: { requiresMetaAuth: true }
    },
    {
      path: '/bookings',
      name: 'BookingHistory',
      component: () => import('@/pages/BookingsHistoryPage.vue'),
      meta: { requiresMetaAuth: true }
    },
    {
      path: '/bookings/qr-code/:type/:id',
      name: 'BookingQRCode',
      component: () => import('@/components/booking/views/BookingQRCodeDrawer.vue'),
      props: true,
      meta: { requiresMetaAuth: true }
    },
    {
      path: '/upload',
      name: 'Upload',
      component: () => import('@/pages/UploadPage.vue'),
      meta: { requiresMetaAuth: true }
    },
    {
      path: '/settings',
      name: 'Settings',
      component: () => import('@/components/settings/SettingsPage.vue'),
      meta: { requiresMetaAuth: true }
    },
    {
      path: '/settings/add-card',
      name: 'AddCard',
      component: () => import('@/components/settings/views/AddCardView.vue'),
      meta: { requiresMetaAuth: true }
    },
    {
      path: '/settings/privacy-and-security',
      name: 'PrivacyAndSecurity',
      component: () => import('@/components/settings/views/SettingsPrivacyandSecurityView.vue'),
      meta: { requiresMetaAuth: true }
    },
    {
      path: '/settings/change-password',
      name: 'ChangePassword',
      component: () => import('@/components/settings/views/SettingsChangePasswordView.vue'),
      meta: { requiresMetaAuth: true }
    },
    {
      path: '/creator/settings',
      name: 'CreatorSettings',
      component: () => import('@/components/settings/SettingsPage.vue'),
      props: { userType: 'creator' },
      meta: { requiresMetaAuth: true }
    },
    // Test pages
    {
      path: '/test/booking-card-create',
      name: 'BookingCardCreateTest',
      component: () => import('@/views/test/BookingCardCreateTestPage.vue'),
      meta: { requiresMetaAuth: false }
    },
    {
      path: '/test/quote-booking-preview',
      name: 'QuoteBookingPreviewTest',
      component: () => import('@/views/test/QuoteBookingPreviewTestPage.vue'),
      meta: { requiresMetaAuth: false }
    },
    {
      path: '/test/booking-card-query',
      name: 'BookingCardQueryTest',
      component: () => import('@/views/test/BookingCardQueryTestPage.vue'),
      meta: { requiresMetaAuth: false }
    },
    {
      path: '/test/booking-card-reply',
      name: 'BookingCardReplyTest',
      component: () => import('@/views/test/BookingCardReplyTestPage.vue'),
      meta: { requiresMetaAuth: false }
    },
    {
      path: '/test/booking-card-sent-request',
      name: 'BookingCardSentRequestTest',
      component: () => import('@/views/test/BookingCardSentRequestTestPage.vue'),
      meta: { requiresMetaAuth: false }
    },
    {
      path: '/test/booking-card-received-request',
      name: 'BookingCardReceivedRequestTest',
      component: () => import('@/views/test/BookingCardReceivedRequestTestPage.vue'),
      meta: { requiresMetaAuth: false }
    },
    {
      path: '/test/reply-message-received',
      name: 'ReplyMessageReceivedTest',
      component: () => import('@/views/test/ReplyMessageReceivedTestPage.vue'),
      meta: { requiresMetaAuth: false }
    },
    {
      path: '/test/reply-message-sent',
      name: 'ReplyMessageSentTest',
      component: () => import('@/views/test/ReplyMessageSentTestPage.vue'),
      meta: { requiresMetaAuth: false }
    },
    {
      path: '/test/quote-preview',
      name: 'QuotePreviewTest',
      component: () => import('@/views/test/QuotePreviewTestPage.vue'),
      meta: { requiresMetaAuth: false }
    },
  ],
});

// Disable browser back button
const disableBrowserBack = () => {
  if (typeof window !== 'undefined') {
    window.history.pushState(null, '', window.location.href);
    
    window.addEventListener('popstate', (event) => {
      event.preventDefault();
      window.history.pushState(null, '', window.location.href);
    }, { passive: false });
  }
};

// Enable browser back button (for development)
const enableBrowserBack = () => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('popstate', () => {});
  }
};

// Initialize browser back disable
if (process.env.NODE_ENV !== 'development') {
  disableBrowserBack();
}

// Route guard for meta-authentication and origin validation
router.beforeEach((to, _from, next) => {
  // Skip middleware if disabled for development
  if (!ENABLE_META_AUTH_MIDDLEWARE) {
    return handleNavigation(to, _from, next);
  }

  const metaAuthStore = useMetaAuthStore();

  // Check if route requires meta-auth
  if (to.meta.requiresMetaAuth && !metaAuthStore.isMetaAuthenticated) {
    sessionStorage.setItem('intendedRoute', to.fullPath);
    return next('/meta-auth');
  }

  // If already authenticated and trying to access meta-auth page, redirect to home
  if (to.path === '/meta-auth' && metaAuthStore.isMetaAuthenticated) {
    return next('/');
  }

  return handleNavigation(to, _from, next);
});

function handleNavigation(to: any, _from: any, next: any) {
  const domainStateStore = useDomainStateStore();
  
  // Check if this is a top-level route (bottom nav)
  const isTopLevel = TOP_LEVEL_ROUTES.includes(to.path);
  
  if (isTopLevel) {
    // Clear all domain stacks when entering via bottom nav
    domainStateStore.clearAllStacks();
    return next();
  }
  
  // For non-top-level routes, require origin
  const hasOrigin = to.query?.origin !== undefined || to.state?.origin !== undefined;
  
  if (!hasOrigin) {
    // Check if this is a test page (allowed without origin)
    if (to.path.startsWith('/test/')) {
      return next();
    }
    
    // Check if this is a child route of a known parent
    const parentRoutes = ['/foryou/search', '/chat/dm/', '/chat/group/', '/chat/new', '/profile/edit', '/location', '/booking', '/settings/', '/user-v2/', '/user/', '/auth'];
    const isChildRoute = parentRoutes.some(parent => to.path.startsWith(parent));
    
    if (isChildRoute) {
      return next();
    }
    
    console.warn(`Navigation to ${to.path} blocked: no origin specified`);
    return next('/foryou');
  }
  
  // Validate origin is valid
  const validOrigins = ['foryou', 'search', 'chat', 'profile', 'bookings', 'settings', 'creator', 'deeplink', 'auth'];
  const origin = to.query.origin || to.state.origin;
  if (!validOrigins.includes(origin)) {
    return next('/foryou');
  }
  
  // Update active domain based on origin
  const domainMap: Record<string, string> = {
    'foryou': 'foryou',
    'search': 'search',
    'chat': 'chat',
    'profile': 'profile',
    'bookings': 'bookingHistory',
    'settings': 'settings',
    'creator': 'profile'
  };
  
  if (to.query.fromDomain || to.state.fromDomain) {
    domainStateStore.setActiveDomain(to.query.fromDomain || to.state.fromDomain);
  } else if (to.state.origin && domainMap[to.state.origin]) {
    domainStateStore.setActiveDomain(domainMap[to.state.origin] as any);
  }
  
  return next();
}

// Remove global tracking - now handled by navigation composables
// router.afterEach((to) => { ... });

export default router;
export { disableBrowserBack, enableBrowserBack };
